import PageLoader from "components/PageLoader"
import { useAppSelector } from "hooks/storeHooks"
import { getFontsTemplate, UICustomizationFonts } from "pages/UICustomization/UICustomizationFonts"
import { UICustomizationLogo } from "pages/UICustomization/UICustomizationLogo"
import {
  getStylesTemplate,
  UICustomizationStyles,
} from "pages/UICustomization/UICustomizationStyles"
import { type UICustomizationData } from "pages/UICustomization/types.ts"
import { useEffect, useEffectEvent, useState } from "react"
import { useTranslation } from "react-i18next"
import { getTintVTOService } from "services/tintvto.ts"
import { typedEnv } from "../../config/typedEnv"
import { Check, Eye, Loader2 } from "lucide-react"
import { useAlert } from "providers/AlertProvider"
import { Button } from "@/components/ui/button"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { getGenericUserErrorMessage } from "@/utils/userFacingErrors"
import { parseUICustomizationStyles } from "./uiCustomizationState"

const getTabsTemplate = (t: (value: string) => string) => [
  {
    title: t("uiCustomization.logo"),
    subTitle: t("uiCustomization.yourCompanyLogo"),
    key: "logo",
    Component: UICustomizationLogo,
  },
  {
    title: t("uiCustomization.styles"),
    subTitle: t("uiCustomization.colorAndSizes"),
    key: "styles",
    Component: UICustomizationStyles,
  },
  {
    title: t("uiCustomization.fonts"),
    subTitle: t("uiCustomization.fontsCustomization"),
    key: "fonts",
    Component: UICustomizationFonts,
  },
]

export const UICustomization = () => {
  const service = getTintVTOService()
  const { showAlert } = useAlert()
  const merchantId = useAppSelector((state) => state.user.userInfo.merchantId)
  const { t } = useTranslation()
  const tabsTemplate = getTabsTemplate(t)

  const [currentTab, setCurrentTab] = useState(tabsTemplate[0]?.key || "")
  const [data, setData] = useState<UICustomizationData>({
    logos: {
      main: undefined,
      short: undefined,
      mainBase64: undefined,
      shortBase64: undefined,
    },
    styles: {},
  })
  const [loading, setLoading] = useState(false)
  const [validData, setValidData] = useState<Record<string, boolean>>(
    tabsTemplate.reduce<Record<string, boolean>>((acc, { key }) => ({ ...acc, [key]: true }), {})
  )
  const [saveLoading, setSaveLoading] = useState(false)
  const valid = Object.values(validData).every((value) => value)
  const reportLoadError = useEffectEvent(() => {
    showAlert({
      error: true,
      text: getGenericUserErrorMessage(t),
    })
  })

  useEffect(() => {
    if (!merchantId) return
    let cancelled = false
    const mainLogoUrl = `https://${typedEnv.VITE_TINT_MERCHANT_CDN}/${merchantId}/logo.png`
    const shortLogoUrl = `https://${typedEnv.VITE_TINT_MERCHANT_CDN}/${merchantId}/short-logo.png`

    void (async () => {
      setLoading(true)
      // Fail closed across merchant switches: a failed load must never leave
      // the previous merchant's theme or logos available to save here.
      setData({
        logos: {
          main: undefined,
          short: undefined,
          mainBase64: undefined,
          shortBase64: undefined,
        },
        styles: {},
      })
      try {
        const [mainLogoResult, shortLogoResult, uiCustomizationResult] = await Promise.allSettled([
          fetch(mainLogoUrl),
          fetch(shortLogoUrl),
          service.getUICustomization(merchantId),
        ])

        if (cancelled) return

        let nextStyles: UICustomizationData["styles"] | undefined
        if (uiCustomizationResult.status === "fulfilled")
          try {
            nextStyles = parseUICustomizationStyles(uiCustomizationResult.value.data.getStyles)
          } catch {
            reportLoadError()
          }
        else reportLoadError()

        setData((prevData) => ({
          ...prevData,
          ...(nextStyles !== undefined ? { styles: nextStyles } : {}),
          logos: {
            main:
              mainLogoResult.status === "fulfilled" && mainLogoResult.value.ok
                ? mainLogoUrl
                : undefined,
            short:
              shortLogoResult.status === "fulfilled" && shortLogoResult.value.ok
                ? shortLogoUrl
                : undefined,
            mainBase64: undefined,
            shortBase64: undefined,
          },
        }))
      } catch {
        if (cancelled) return

        reportLoadError()
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [merchantId, service])

  useEffect(() => {
    const fontsValid = getFontsTemplate(t).every(
      ({ keyFamily, keyStyle, keyWeight }) =>
        data.styles[keyFamily] !== "" &&
        data.styles[keyStyle] !== "" &&
        data.styles[keyWeight] !== "" &&
        data.styles[keyFamily] &&
        data.styles[keyStyle] &&
        data.styles[keyWeight]
    )

    let stylesValid = true
    getStylesTemplate(t).forEach(({ styles }) => {
      if (!styles.every(({ key }) => !key || data.styles[key] !== "")) stylesValid = false
    })
    setValidData((prevValidData) => ({
      ...prevValidData,
      fonts: fontsValid,
      styles: stylesValid,
    }))
  }, [data.styles, t])

  const onSave = async () => {
    if (!merchantId) return

    setSaveLoading(true)
    try {
      await service.updateUICustomization({
        merchantId,
        data: data.styles,
        mainLogo: data.logos.mainBase64,
        shortLogo: data.logos.shortBase64,
      })

      showAlert({ error: false, text: t("uiCustomization.saved") })
    } catch {
      showAlert({
        error: true,
        text: getGenericUserErrorMessage(t),
      })
    } finally {
      setSaveLoading(false)
    }
  }

  return (
    <div className="p-6" data-testid="ui-customization-page">
      <div className="mx-auto max-w-[1440px]">
        <div className="mb-6">
          <h1
            className="text-2xl font-semibold tracking-tight"
            data-testid="ui-customization-title"
          >
            {t("uiCustomization.uiCustomizationMerchantName")}
          </h1>
        </div>

        {loading ? (
          <PageLoader variant="section" />
        ) : (
          <Tabs value={currentTab} onValueChange={setCurrentTab}>
            <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <TabsList className="w-fit" data-testid="ui-customization-tabs">
                {tabsTemplate.map(({ title, key }) => (
                  <TabsTrigger
                    key={key}
                    value={key}
                    className={!validData[key] ? "text-destructive" : ""}
                    data-testid={`ui-customization-tab-${key}`}
                  >
                    {title}
                  </TabsTrigger>
                ))}
              </TabsList>

              <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                <Button variant="outline" size="sm" asChild>
                  <a
                    href={`https://${typedEnv.VITE_TINT_WIDGET_LINK}/?merchantId=${merchantId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="ui-customization-preview"
                  >
                    <Eye className="mr-1.5 size-3.5" />
                    {t("previewTintVto")}
                  </a>
                </Button>
                <Button
                  size="sm"
                  onClick={onSave}
                  disabled={!valid || saveLoading}
                  data-testid="ui-customization-save"
                >
                  {saveLoading ? (
                    <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                  ) : (
                    <Check className="mr-1.5 size-3.5" />
                  )}
                  {t("uiCustomization.save")}
                </Button>
              </div>
            </div>

            {tabsTemplate.map(({ Component, key }) => (
              <TabsContent key={key} value={key} data-testid={`ui-customization-tab-panel-${key}`}>
                <Component data={data} onChange={setData} />
              </TabsContent>
            ))}
          </Tabs>
        )}
      </div>
    </div>
  )
}
